/**
 * The project management contract — projects, membership, milestones, tasks, and hours-only analytics.
 *
 * Copies employee.contract.ts's / organisation.contract.ts's conventions exactly: DTOs distinct
 * from the Prisma models, `paginatedSchema()` for every list, effective-dated rows (`ProjectMember`)
 * carry `validFrom`/`validTo` rather than being overwritten (invariant 5).
 *
 * MONEY IS OUT OF V1 (plan D16). Rate cards, the capacity grid, project budget AMOUNT, milestone
 * billing amounts and revenue/cost/margin are all deferred; they return later as their own
 * contract-first PRs, behind a `project.financials` permission gate. `budgetHours` stays.
 * `ResourceAllocation` (Phase 6) carries `allocationPercent`, never a rate or amount, so it ships
 * ahead of that gate.
 *
 * TASKS ARE FLAT, NOT NESTED UNDER `/:projectId`. A `ProjectTask` always belongs to exactly one
 * project, but "My Tasks" is a person's board across every project they're assigned in, which
 * nesting under `/:projectId` cannot express without a second endpoint, so `listTasks` takes
 * `projectId` as an optional filter and `createTask` takes it in the body. `ProjectMember` and
 * `ProjectMilestone` have no cross-project screen, so they stay nested.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { routeMeta } from './meta.js';
import {
  dateOnlySchema,
  decimalStringSchema,
  errorBodySchema,
  instantSchema,
  legalEntityIdSchema,
  projectMilestoneStatusSchema,
  orgIdSchema,
  paginatedSchema,
  paginationQuerySchema,
  projectBillingModelSchema,
  projectPhaseStatusSchema,
  projectRoleSchema,
  projectStatusSchema,
  taskPrioritySchema,
  taskStatusSchema,
  uuidSchema,
} from '../schemas/common.js';
import { TASK_ORIGIN_KINDS, TASK_STATUS_BUCKETS } from '../enums.js';

const c = initContract();

// ---------------------------------------------------------------------------
// DTOs
// ---------------------------------------------------------------------------

/** Manager-asserted, deliberately not derived from status/dates/budget — see schema.prisma's model. */
const projectHealthSchema = z.enum(['green', 'amber', 'red']);

/**
 * Peer task visibility on this project. `shared` (default): every member with project-level read
 * access sees every task, unchanged from pre-toggle behavior. `isolated`: task-returning/accepting
 * endpoints restrict to the viewer's own roster scope — see PM_FINAL_SCOPE_SPECIFICATION.md Part
 * 3/4. Never applies to a system project, whose isolation (`isSystemProject`) is independent of
 * this field.
 */
export const taskVisibilitySchema = z.enum(['shared', 'isolated']);

/** Daily Work Planning Phase 1 — see `TASK_ORIGIN_KINDS`'s own doc comment in enums.ts. */
export const taskOriginKindSchema = z.enum(TASK_ORIGIN_KINDS);

/**
 * Daily Work Planning Phase 2 — a non-negative `decimalStringSchema`, same convention as
 * `employee.contract.ts`'s/`offboarding.contract.ts`'s own local `nonNegativeDecimalStringSchema`.
 */
const nonNegativeDecimalSchema = decimalStringSchema.refine(
  (v) => Number.parseFloat(v) >= 0,
  'Must not be negative',
);

/**
 * Daily Work Planning Phase 2 — `targetValue`/`achievedValue` require `unit` the moment EITHER is
 * set (plan §3). Shared by `createTaskBodySchema` and `updateTaskBodySchema` via `.superRefine`,
 * same pattern as this file's other cross-field checks (e.g. `createResourceAllocationBodySchema`).
 */
function checkTargetUnitPair(
  val: { targetValue?: string | null; achievedValue?: string | null; unit?: string | null },
  ctx: z.RefinementCtx,
): void {
  const hasValue =
    (val.targetValue !== undefined && val.targetValue !== null) ||
    (val.achievedValue !== undefined && val.achievedValue !== null);
  const hasUnit = val.unit !== undefined && val.unit !== null && val.unit !== '';
  if (hasValue && !hasUnit) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'unit is required when targetValue or achievedValue is set',
      path: ['unit'],
    });
  }
}

export const projectSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  legalEntityId: uuidSchema.nullable(),
  departmentId: uuidSchema.nullable(),
  code: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  clientName: z.string().nullable(),
  costCentre: z.string().nullable(),
  billingModel: projectBillingModelSchema,
  status: projectStatusSchema,
  health: projectHealthSchema.nullable(),
  startDate: dateOnlySchema.nullable(),
  endDate: dateOnlySchema.nullable(),
  ownerPersonId: uuidSchema.nullable(),
  ownerName: z.string().nullable(),
  budgetHours: decimalStringSchema.nullable(),
  isBillable: z.boolean(),
  /** A platform-provisioned project (the Internal / Non-project bucket): read-only for users. */
  isSystem: z.boolean(),
  taskVisibility: taskVisibilitySchema,
  requireCompletionReview: z.boolean(),
  createdAt: instantSchema,
  updatedAt: instantSchema,
});
export type Project = z.infer<typeof projectSchema>;

export const projectMemberSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  projectId: uuidSchema,
  personId: uuidSchema,
  personName: z.string(),
  projectRole: projectRoleSchema,
  validFrom: dateOnlySchema,
  validTo: dateOnlySchema.nullable(),
});
export type ProjectMember = z.infer<typeof projectMemberSchema>;

export const projectMilestoneSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  projectId: uuidSchema,
  name: z.string(),
  description: z.string().nullable(),
  dueDate: dateOnlySchema.nullable(),
  completedOn: dateOnlySchema.nullable(),
  status: projectMilestoneStatusSchema,
  sortOrder: z.number().int(),
  /**
   * Task roll-up, only on `listMilestones`: every task on the milestone except the cancelled bucket.
   * OMITTED (not zero) when the caller cannot see every task of the project — an isolated project
   * narrows a restricted caller to their own people's tasks, and a partial aggregate would be both
   * misleading and a side channel to the hidden tasks. Also absent on create/update responses.
   */
  taskCount: z.number().int().nonnegative().optional(),
  /** Of `taskCount`, how many sit in a completed-bucket status (`TaskStatusDefinition.bucket`). */
  doneCount: z.number().int().nonnegative().optional(),
});
export type ProjectMilestone = z.infer<typeof projectMilestoneSchema>;

/**
 * PM-005 (Keka gap closure, follow-up round 3) — lightweight grouping ABOVE milestones. Orthogonal
 * to `ProjectMilestone`: a task may carry a `phaseId` and/or a `milestoneId` independently. Same
 * roll-up-count shape and same leak-safety rule as `projectMilestoneSchema.taskCount`/`doneCount`
 * (see `milestoneTaskCounts`'s doc comment — `phaseTaskCounts` mirrors it exactly).
 */
export const projectPhaseSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  projectId: uuidSchema,
  name: z.string(),
  description: z.string().nullable(),
  status: projectPhaseStatusSchema,
  sortOrder: z.number().int(),
  startDate: dateOnlySchema.nullable(),
  endDate: dateOnlySchema.nullable(),
  taskCount: z.number().int().nonnegative().optional(),
  doneCount: z.number().int().nonnegative().optional(),
  createdAt: instantSchema,
  updatedAt: instantSchema,
});
export type ProjectPhase = z.infer<typeof projectPhaseSchema>;

/** A per-organisation, HR-configurable task classification. Deactivated rather than deleted. */
export const taskTypeSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  code: z.string(),
  name: z.string(),
  isActive: z.boolean(),
  sortOrder: z.number().int(),
  requireEvidence: z.boolean(),
});
export type TaskType = z.infer<typeof taskTypeSchema>;

export const projectTaskSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  projectId: uuidSchema,
  projectName: z.string(),
  milestoneId: uuidSchema.nullable(),
  /** PM-005 — orthogonal to milestoneId, see `projectPhaseSchema`'s doc comment. */
  phaseId: uuidSchema.nullable(),
  parentTaskId: uuidSchema.nullable(),
  taskTypeId: uuidSchema.nullable(),
  taskTypeName: z.string().nullable(),
  /**
   * True only when the task has a task type AND that type has `requireEvidence = true`; false for
   * an untyped task. Not sensitive (it only says "this type needs evidence") — served on every task
   * the caller can already read, so a plain assignee can gate "Mark done" without `project.task:manage`
   * (which `listTaskTypes` needs).
   */
  taskTypeRequiresEvidence: z.boolean(),
  kpiId: uuidSchema.nullable(),
  kpiName: z.string().nullable(),
  goalId: uuidSchema.nullable(),
  goalTitle: z.string().nullable(),
  code: z.string().nullable(),
  title: z.string(),
  description: z.string().nullable(),
  status: taskStatusSchema,
  priority: taskPrioritySchema,
  assigneePersonId: uuidSchema.nullable(),
  assigneeName: z.string().nullable(),
  startDate: dateOnlySchema.nullable(),
  dueDate: dateOnlySchema.nullable(),
  completedOn: dateOnlySchema.nullable(),
  estimateHours: decimalStringSchema.nullable(),
  isBillable: z.boolean(),
  sortOrder: z.number().int(),
  /** ADR 0020 §4: 0-100, only meaningful for a task spanning more than one day. Null = not tracked. */
  progressPercent: z.number().int().min(0).max(100).nullable(),
  /** Daily Work Planning Phase 1 — how this task came to exist. Null = legacy/pre-existing task,
   * deliberately not backfill-guessed (Decision 3) — the UI shows no badge for null. */
  originKind: taskOriginKindSchema.nullable(),
  /** Daily Work Planning Phase 2 — optional measurable progress, independent of `progressPercent`.
   * `unit` is required by the server whenever either value is set; `remainingValue` is derived on
   * the client (targetValue - achievedValue) and never sent over the wire. */
  targetValue: decimalStringSchema.nullable(),
  achievedValue: decimalStringSchema.nullable(),
  unit: z.string().nullable(),
  /** Daily Work Planning Phase 5 — opt-in link to an `EmployeeTarget`; this task's `achievedValue`
   * contributes to that target's achievement only when both are set (project.ts's
   * `computeEmployeeTargetAchievement`). Manager/HR-set only, same rule as `kpiId` above — an
   * assignee's self-service carve-out does not cover this field. */
  employeeTargetId: uuidSchema.nullable(),
  /** Daily Work Planning Phase 7 — how many `TaskCarryForwardRecord` rows exist for this task (the
   * SAME task row is reused on every carry-forward, never duplicated — see `carryForwardTask`), so
   * "carried forward N times" is servable from the task response itself rather than a second query
   * per task. 0 for a task never carried forward. */
  carryForwardCount: z.number().int().nonnegative(),
  createdAt: instantSchema,
  updatedAt: instantSchema,
});
export type ProjectTask = z.infer<typeof projectTaskSchema>;

/**
 * PM-033 (Keka gap closure, follow-up round 3) — a SECONDARY assignee on a task: can see/act on it
 * but is not THE assignee of record. `ProjectTask.assigneePersonId` stays the single primary —
 * completion review, evidence, extension requests and every other assignee-scoped rule keep
 * reading only that field, deliberately unchanged by this table's existence.
 */
export const taskAdditionalAssigneeSchema = z.object({
  id: uuidSchema,
  taskId: uuidSchema,
  personId: uuidSchema,
  personName: z.string(),
  addedAt: instantSchema,
});
export type TaskAdditionalAssignee = z.infer<typeof taskAdditionalAssigneeSchema>;

// ---- Analytics ----------------------------------------------------------------

export const projectAnalyticsSchema = z.object({
  projectId: uuidSchema,
  from: dateOnlySchema,
  to: dateOnlySchema,
  percentComplete: z.number().min(0).max(100),
  budgetHours: decimalStringSchema.nullable(),
  loggedHours: decimalStringSchema,
  billableHours: decimalStringSchema,
  /** Sum of every task's estimate on the project, so the page can show logged hours against what was planned. */
  taskEstimateHours: decimalStringSchema,
  /** Estimate-at-completion, in hours — derived from current burn rate, not stored. */
  estimatedHoursAtCompletion: decimalStringSchema.nullable(),
  taskCounts: z.object({
    total: z.number().int().nonnegative(),
    done: z.number().int().nonnegative(),
    overdue: z.number().int().nonnegative(),
  }),
});
export type ProjectAnalytics = z.infer<typeof projectAnalyticsSchema>;

// ---------------------------------------------------------------------------
// Requests
// ---------------------------------------------------------------------------

/** Matches org-setup.contract.ts / organisation.contract.ts's own local `deleteResultSchema` — the
 * 200-confirmation-body DELETE convention (never a bare 204). */
const deleteResultSchema = z.object({ success: z.boolean() });

// ---- Projects -----------------------------------------------------------------

export const listProjectsQuerySchema = paginationQuerySchema.extend({
  /** Matches code, name, or client name. */
  q: z.string().trim().max(200).optional(),
  status: projectStatusSchema.optional(),
  legalEntityId: legalEntityIdSchema.optional(),
  departmentId: uuidSchema.optional(),
  billingModel: projectBillingModelSchema.optional(),
  ownerPersonId: uuidSchema.optional(),
  /** Only projects the caller is a `ProjectMember` of — what "My Projects" filters on. */
  mine: z.coerce.boolean().optional(),
});

export const createProjectBodySchema = z
  .object({
    legalEntityId: uuidSchema.optional(),
    departmentId: uuidSchema.optional(),
    code: z.string().trim().min(1).max(30),
    name: z.string().trim().min(1).max(200),
    description: z.string().trim().max(5000).optional(),
    clientName: z.string().trim().max(200).optional(),
    costCentre: z.string().trim().max(50).optional(),
    billingModel: projectBillingModelSchema.default('non_billable'),
    startDate: dateOnlySchema.optional(),
    endDate: dateOnlySchema.optional(),
    ownerPersonId: uuidSchema.optional(),
    budgetHours: decimalStringSchema.optional(),
    isBillable: z.boolean().default(false),
    requireCompletionReview: z.boolean().default(false),
  })
  .refine((body) => !body.endDate || !body.startDate || body.endDate >= body.startDate, {
    message: 'endDate must be on or after startDate',
    path: ['endDate'],
  });

export const updateProjectBodySchema = z.object({
  legalEntityId: uuidSchema.nullable().optional(),
  departmentId: uuidSchema.nullable().optional(),
  name: z.string().trim().min(1).max(200).optional(),
  description: z.string().trim().max(5000).nullable().optional(),
  clientName: z.string().trim().max(200).nullable().optional(),
  costCentre: z.string().trim().max(50).nullable().optional(),
  billingModel: projectBillingModelSchema.optional(),
  status: projectStatusSchema.optional(),
  health: projectHealthSchema.nullable().optional(),
  taskVisibility: taskVisibilitySchema.optional(),
  requireCompletionReview: z.boolean().optional(),
  startDate: dateOnlySchema.nullable().optional(),
  endDate: dateOnlySchema.nullable().optional(),
  ownerPersonId: uuidSchema.nullable().optional(),
  budgetHours: decimalStringSchema.nullable().optional(),
  isBillable: z.boolean().optional(),
});

// ---- Members --------------------------------------------------------------

export const listMembersQuerySchema = paginationQuerySchema.extend({
  /** Defaults to current members only (`validTo` null or in the future). */
  includeEnded: z.coerce.boolean().default(false),
});

export const addMemberBodySchema = z.object({
  personId: uuidSchema,
  projectRole: projectRoleSchema.default('member'),
  validFrom: dateOnlySchema,
});

/** POST, not DELETE — ending membership is a fact with a date, not a row deletion (invariant 5). */
export const endMemberBodySchema = z.object({
  validTo: dateOnlySchema,
});

/**
 * Add several people at once, all with the same role and start date — the common "onboard the
 * whole team" case. Each person is still added through the exact same rules `addMember` enforces
 * (authorization, current-membership conflict); a person already a current member is skipped, not
 * an error for the whole batch, so one existing membership can never block adding the rest.
 */
export const addMembersBulkBodySchema = z.object({
  personIds: z.array(uuidSchema).min(1).max(100),
  projectRole: projectRoleSchema.default('member'),
  validFrom: dateOnlySchema,
});

export const addMembersBulkResponseSchema = z.object({
  added: z.array(projectMemberSchema),
  skipped: z.array(z.object({ personId: uuidSchema, reason: z.string() })),
});

// ---- Milestones -------------------------------------------------------------

export const listMilestonesQuerySchema = paginationQuerySchema.extend({
  status: projectMilestoneStatusSchema.optional(),
});

export const createMilestoneBodySchema = z.object({
  name: z.string().trim().min(1).max(200),
  description: z.string().trim().max(2000).optional(),
  dueDate: dateOnlySchema.optional(),
  sortOrder: z.number().int().optional(),
});

export const updateProjectMilestoneBodySchema = z.object({
  name: z.string().trim().min(1).max(200).optional(),
  description: z.string().trim().max(2000).nullable().optional(),
  dueDate: dateOnlySchema.nullable().optional(),
  completedOn: dateOnlySchema.nullable().optional(),
  status: projectMilestoneStatusSchema.optional(),
  sortOrder: z.number().int().optional(),
});

// ---- Phases (PM-005) ---------------------------------------------------------

export const listPhasesQuerySchema = paginationQuerySchema.extend({
  status: projectPhaseStatusSchema.optional(),
});

export const createPhaseBodySchema = z.object({
  name: z.string().trim().min(1).max(200),
  description: z.string().trim().max(2000).optional(),
  startDate: dateOnlySchema.optional(),
  endDate: dateOnlySchema.optional(),
  sortOrder: z.number().int().optional(),
});

export const updatePhaseBodySchema = z.object({
  name: z.string().trim().min(1).max(200).optional(),
  description: z.string().trim().max(2000).nullable().optional(),
  startDate: dateOnlySchema.nullable().optional(),
  endDate: dateOnlySchema.nullable().optional(),
  status: projectPhaseStatusSchema.optional(),
  sortOrder: z.number().int().optional(),
});

// ---- Tasks ------------------------------------------------------------------

export const listTasksQuerySchema = paginationQuerySchema.extend({
  projectId: uuidSchema.optional(),
  milestoneId: uuidSchema.optional(),
  phaseId: uuidSchema.optional(),
  /** Defaults to the caller when omitted from the "My Tasks" board; unset entirely means "any assignee". */
  assigneePersonId: uuidSchema.optional(),
  status: taskStatusSchema.optional(),
  priority: taskPrioritySchema.optional(),
  /** Daily Work Planning Phase 1 — filter by how the task came to exist. Unset = any origin
   * (including legacy tasks with `originKind: null`). */
  originKind: taskOriginKindSchema.optional(),
});

export const createTaskBodySchema = z
  .object({
    projectId: uuidSchema,
    milestoneId: uuidSchema.optional(),
    phaseId: uuidSchema.optional(),
    parentTaskId: uuidSchema.optional(),
    taskTypeId: uuidSchema.optional(),
    kpiId: uuidSchema.optional(),
    goalId: uuidSchema.optional(),
    code: z.string().trim().max(50).optional(),
    title: z.string().trim().min(1).max(300),
    description: z.string().trim().max(5000).optional(),
    status: taskStatusSchema.optional(),
    priority: taskPrioritySchema.default('medium'),
    assigneePersonId: uuidSchema.optional(),
    startDate: dateOnlySchema.optional(),
    dueDate: dateOnlySchema.optional(),
    estimateHours: decimalStringSchema.optional(),
    isBillable: z.boolean().default(true),
    sortOrder: z.number().int().optional(),
    /** Daily Work Planning Phase 2 — optional measurable target/achieved, independent of
     * `progressPercent`. `unit` is required the moment either is set (see `checkTargetUnitPair`). */
    targetValue: nonNegativeDecimalSchema.optional(),
    achievedValue: nonNegativeDecimalSchema.optional(),
    unit: z.string().trim().min(1).max(40).optional(),
    /** Daily Work Planning Phase 5 — opt-in link to an `EmployeeTarget`, manager/HR-set only (same
     * rule as `kpiId` above; `createTask` as a whole already requires `project.task:manage`). */
    employeeTargetId: uuidSchema.optional(),
  })
  .superRefine(checkTargetUnitPair)
  // PM audit fix 1 — same rule `createProjectBodySchema` already enforces for a project's own
  // startDate/endDate (this file, above). A create body always carries both dates in the same
  // request (never a partial patch), so a plain `.refine` over the raw body is enough here —
  // unlike `updateTaskBodySchema` below, which needs the EFFECTIVE merged state instead.
  .refine((body) => !body.dueDate || !body.startDate || body.dueDate >= body.startDate, {
    message: 'dueDate must be on or after startDate',
    path: ['dueDate'],
  });

/**
 * Every field optional so ONE schema serves both the manager's full edit and the assignee's
 * self-service move. The route enforces the actual carve-out (§9 of the plan): with
 * `project.task:manage` any field may be sent; without it, only a PATCH whose body is `{status}`
 * and nothing else is accepted, and only when `assigneePersonId` is the caller — a schema alone
 * cannot express "these fields together, conditioned on who's asking".
 */
export const updateTaskBodySchema = z.object({
  milestoneId: uuidSchema.nullable().optional(),
  phaseId: uuidSchema.nullable().optional(),
  parentTaskId: uuidSchema.nullable().optional(),
  taskTypeId: uuidSchema.nullable().optional(),
  kpiId: uuidSchema.nullable().optional(),
  goalId: uuidSchema.nullable().optional(),
  code: z.string().trim().max(50).nullable().optional(),
  title: z.string().trim().min(1).max(300).optional(),
  description: z.string().trim().max(5000).nullable().optional(),
  status: taskStatusSchema.optional(),
  priority: taskPrioritySchema.optional(),
  assigneePersonId: uuidSchema.nullable().optional(),
  /**
   * PM audit fix 1 — NO `.refine` here for "dueDate >= startDate", unlike `createTaskBodySchema`'s
   * raw-body check above: this is a PARTIAL patch, so a body touching only `dueDate` (or only
   * `startDate`) must be validated against the EFFECTIVE merged state (existing row + this patch),
   * which the schema cannot see. `updateTask` itself checks this, same division of labor its own
   * `targetValue`/`achievedValue`/`unit` cross-check already establishes just below.
   */
  startDate: dateOnlySchema.nullable().optional(),
  dueDate: dateOnlySchema.nullable().optional(),
  completedOn: dateOnlySchema.nullable().optional(),
  estimateHours: decimalStringSchema.nullable().optional(),
  isBillable: z.boolean().optional(),
  sortOrder: z.number().int().optional(),
  progressPercent: z.number().int().min(0).max(100).nullable().optional(),
  /**
   * Daily Work Planning Phase 2. `targetValue`/`unit` are manager/HR-set only — the route's
   * self-service carve-out (project.ts's `updateTask`) accepts `achievedValue` from the task's own
   * assignee but rejects `targetValue`/`unit` from that same caller (plan §3/§10). The schema
   * itself cannot express "which fields, conditioned on who's asking" — same division of labor
   * `updateTaskBodySchema`'s own header comment already documents for `status`/`progressPercent`.
   *
   * NO `.superRefine` here for "unit required if either is set", unlike `createTaskBodySchema`:
   * this is a PARTIAL patch, so a body of just `{achievedValue}` (exactly the self-service shape)
   * is valid on its own when the task ALREADY has a unit from when it was created — the schema
   * cannot see the existing row to know that. `updateTask` itself checks the EFFECTIVE merged
   * state (existing row + this patch) and 422s there instead.
   */
  targetValue: nonNegativeDecimalSchema.nullable().optional(),
  achievedValue: nonNegativeDecimalSchema.nullable().optional(),
  unit: z.string().trim().min(1).max(40).nullable().optional(),
  /** Daily Work Planning Phase 5 — opt-in link to an `EmployeeTarget`. Manager/HR-set only, same
   * rule as `kpiId` above: the route's self-service carve-out (`status`/`progressPercent`/
   * `achievedValue` only) does NOT cover this field, so sending it without `project.task:manage`
   * falls through to the ordinary 403, exactly like sending `targetValue` does today. */
  employeeTargetId: uuidSchema.nullable().optional(),
});

// ---- Additional assignees (PM-033) -------------------------------------------------

export const addTaskAssigneeBodySchema = z.object({
  personId: uuidSchema,
});

// ---- Task types --------------------------------------------------------------------

export const listTaskTypesQuerySchema = z.object({
  /** Inactive types are hidden from pickers; an admin screen asks for them all. */
  includeInactive: z.coerce.boolean().optional(),
});

export const createTaskTypeBodySchema = z.object({
  /** Stable machine key. Immutable after creation, unique per organisation. */
  code: z
    .string()
    .trim()
    .min(1)
    .max(50)
    .regex(/^[A-Za-z0-9_-]+$/, 'Use letters, digits, underscore or hyphen'),
  name: z.string().trim().min(1).max(100),
  sortOrder: z.number().int().optional(),
  requireEvidence: z.boolean().optional(),
});

export const updateTaskTypeBodySchema = z.object({
  name: z.string().trim().min(1).max(100).optional(),
  isActive: z.boolean().optional(),
  sortOrder: z.number().int().optional(),
  requireEvidence: z.boolean().optional(),
});

// ---- Task templates (Phase 5 — reusable item sets applied onto a project) ------------

/** One line item on a template. `taskTypeId` may point at a since-deactivated `TaskType`; the
 * template keeps the link (only `applyTaskTemplate` skips it, with a warning) rather than dropping
 * it silently. */
export const taskTemplateItemSchema = z.object({
  id: uuidSchema,
  title: z.string(),
  description: z.string().nullable(),
  taskTypeId: uuidSchema.nullable(),
  taskTypeName: z.string().nullable(),
  estimateHours: decimalStringSchema.nullable(),
  /** PM-027 — null = createTask's own default ('medium') applies when the item is turned into a task. */
  priority: taskPrioritySchema.nullable(),
  /** PM-027 — offset in days from the apply reference date; `dueDate = applyDate + relativeDueDays`. */
  relativeDueDays: z.number().int().nullable(),
  sortOrder: z.number().int(),
});
export type TaskTemplateItem = z.infer<typeof taskTemplateItemSchema>;

export const taskTemplateSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  name: z.string(),
  /** Computed server-side from the template's current item set — not a stored column. */
  itemCount: z.number().int(),
  createdAt: instantSchema,
  updatedAt: instantSchema,
});
export type TaskTemplate = z.infer<typeof taskTemplateSchema>;

export const taskTemplateWithItemsSchema = taskTemplateSchema.extend({
  items: z.array(taskTemplateItemSchema),
});
export type TaskTemplateWithItems = z.infer<typeof taskTemplateWithItemsSchema>;

const taskTemplateItemInputSchema = z.object({
  title: z.string().trim().min(1).max(200),
  description: z.string().trim().max(5000).optional(),
  taskTypeId: uuidSchema.optional(),
  estimateHours: decimalStringSchema.optional(),
  priority: taskPrioritySchema.optional(),
  relativeDueDays: z.number().int().min(-3650).max(3650).optional(),
  sortOrder: z.number().int().optional(),
});

export const createProjectTaskTemplateBodySchema = z.object({
  name: z.string().trim().min(1).max(200),
  items: z.array(taskTemplateItemInputSchema).min(1),
});

/** All-optional at the top level; when `items` is provided it REPLACES the template's whole set,
 * not a merge — matches how `updateTaskType`/`updateKpi` treat their own optional bodies, except
 * `items` here is deliberately whole-collection-replace rather than field-level patch. */
export const updateProjectTaskTemplateBodySchema = z.object({
  name: z.string().trim().min(1).max(200).optional(),
  items: z.array(taskTemplateItemInputSchema).min(1).optional(),
});

export const applyProjectTaskTemplateBodySchema = z.object({
  projectId: uuidSchema,
  milestoneId: uuidSchema.optional(),
});

export const applyTaskTemplateResponseSchema = z.object({
  createdTaskIds: z.array(uuidSchema),
  /** Items skipped because they referenced a since-deactivated TaskType/Kpi — never a hard failure
   * of the whole apply. */
  skipped: z.array(z.object({ itemTitle: z.string(), reason: z.string() })),
});

// ---- Bulk task import (Phase 5) -------------------------------------------------------

const importTasksBulkRowSchema = z.object({
  title: z.string().trim().min(1).max(300),
  description: z.string().trim().max(5000).optional(),
  assigneeEmail: z.string().trim().email().optional(),
  /** Date-only, unambiguous ISO (`YYYY-MM-DD`) — the DD/MM vs. MM/DD ambiguity is resolved client-side
   * before this row ever reaches the contract; the server never guesses a locale. */
  dueDate: dateOnlySchema.optional(),
  code: z.string().trim().max(50).optional(),
});

export const importTasksBulkBodySchema = z.object({
  projectId: uuidSchema,
  rows: z.array(importTasksBulkRowSchema).min(1).max(500),
});

export const importTasksBulkResponseSchema = z.object({
  imported: z.number().int(),
  errors: z.array(z.object({ row: z.number().int(), reason: z.string() })),
});

// ---- Resource allocation / capacity planning (Phase 6) ---------------------------------
//
// Visibility scope: `listResourceAllocations` is gated on `project.member:manage`, the same
// project-scoped permission `addMember`/`endMember` already use — a caller only ever sees rows for
// projects they can manage membership on, mirroring the roster-narrowing every other PM endpoint
// applies rather than an org-wide-by-default read. Allocation % is performance-adjacent data.
//
// Overallocation (`totalAllocationPercent > 100`) is a computed WARNING, never a write-time block —
// `createResourceAllocation` has no 409 for it. Only an unknown `personId`/`projectId` 404s.

/** One active or historical assignment of a person's time to a project (invariant 5 — ended by
 * `validTo`, never deleted). */
export const resourceAllocationSchema = z.object({
  id: uuidSchema,
  personId: uuidSchema,
  personName: z.string(),
  projectId: uuidSchema,
  projectName: z.string(),
  allocationPercent: z.number().int(),
  validFrom: dateOnlySchema,
  validTo: dateOnlySchema.nullable(),
  /** Resolved server-side against the caller's own local "today" (invariant 3), the same day every
   * other field on this response is computed against — never recompute this on the client against
   * the browser's UTC date, which can disagree with the server by a day across a timezone boundary
   * and show a closed allocation as still open (or vice versa). */
  isActiveToday: z.boolean(),
});
export type ResourceAllocation = z.infer<typeof resourceAllocationSchema>;

export const listResourceAllocationsQuerySchema = paginationQuerySchema.extend({
  /** The cursor is a personId (the handler keysets on `personId > cursor` against a uuid column),
   * so it is validated as one here — a malformed value is a 422 at the edge, never a Prisma error
   * surfacing as a 500. Overrides the shared free-form `cursor` string on purpose. */
  cursor: uuidSchema.optional(),
  personId: uuidSchema.optional(),
  departmentId: uuidSchema.optional(),
  /** Filters by `Post.name` (job title), not an id — matches how job title is filtered elsewhere
   * in this codebase (see `employee.contract.ts`), not `postId`. */
  jobTitle: z.string().trim().min(1).optional(),
  projectId: uuidSchema.optional(),
  /** Keep only people whose current total allocation leaves at least this much room, e.g. `50` for
   * "at least half-available". Computed server-side from active allocation rows, not stored. */
  minAvailabilityPercent: z.number().int().min(0).max(100).optional(),
});

/** Server-computed bench/overallocation/leave status for one person, not a stored column — see
 * Phase 6's plan for the exact derivation (sum of active `allocationPercent`, cross-referenced
 * against the Leave module's approved-leave-in-range query, never duplicated here). */
export const resourcePersonStatusSchema = z.enum([
  'available',
  'on_bench',
  'overallocated',
  'on_leave',
]);

export const resourcePersonSummarySchema = z.object({
  personId: uuidSchema,
  personName: z.string(),
  departmentName: z.string().nullable(),
  totalAllocationPercent: z.number().int(),
  status: resourcePersonStatusSchema,
  allocations: z.array(resourceAllocationSchema),
});
export type ResourcePersonSummary = z.infer<typeof resourcePersonSummarySchema>;

export const createResourceAllocationBodySchema = z
  .object({
    personId: uuidSchema,
    projectId: uuidSchema,
    allocationPercent: z.number().int().min(1).max(100),
    validFrom: dateOnlySchema,
    validTo: dateOnlySchema.optional(),
  })
  .superRefine((val, ctx) => {
    // ISO yyyy-mm-dd compares correctly as a string; mirrors endResourceAllocation's own check.
    if (val.validTo !== undefined && val.validTo < val.validFrom) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'validTo cannot be before validFrom',
        path: ['validTo'],
      });
    }
  });

/** POST, not DELETE — ending an allocation is a fact with a date, not a row deletion (invariant 5),
 * same as `endMemberBodySchema`. */
export const endResourceAllocationBodySchema = z.object({
  validTo: dateOnlySchema,
});

/**
 * Change an allocation going forward (PM-019). `allocationPercent` is EFFECTIVE-DATED (invariant 5):
 * when the row has already started, the current row is closed the day before `effectiveFrom` and a
 * new row carries the new percent from `effectiveFrom` — the past is never rewritten. `validTo` is
 * the allocation's new end date (`null` = open-ended); on its own (percent unchanged) it just moves
 * the end of the existing row, exactly like `endResourceAllocation`. `effectiveFrom` defaults to the
 * person's today and can never be earlier than it.
 */
export const updateResourceAllocationBodySchema = z
  .object({
    allocationPercent: z.number().int().min(1).max(100).optional(),
    validTo: dateOnlySchema.nullable().optional(),
    effectiveFrom: dateOnlySchema.optional(),
  })
  .superRefine((val, ctx) => {
    if (val.allocationPercent === undefined && val.validTo === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Provide allocationPercent and/or validTo',
        path: ['allocationPercent'],
      });
    }
    if (val.validTo && val.effectiveFrom && val.validTo < val.effectiveFrom) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'validTo cannot be before effectiveFrom',
        path: ['validTo'],
      });
    }
  });

export const updateResourceAllocationResponseSchema = z.object({
  /** The row now in force from the change date (the new row after a split, else the edited row). */
  allocation: resourceAllocationSchema,
  /** The row that was closed the day before `effectiveFrom`; `null` when the edit was in place. */
  superseded: resourceAllocationSchema.nullable(),
  /** The person's total allocation across every project on `effectiveFrom` (after this change). */
  totalAllocationPercent: z.number().int(),
  /** `totalAllocationPercent > 100` — a WARNING, never a rejection (same as `createResourceAllocation`). */
  overallocated: z.boolean(),
});
export type UpdateResourceAllocationResponse = z.infer<typeof updateResourceAllocationResponseSchema>;

/** One allocation row on a project's team-allocation view, with the person's overall availability. */
export const projectAllocationSchema = resourceAllocationSchema.extend({
  /** The person's total allocation across every project as of today. */
  personTotalAllocationPercent: z.number().int(),
  /** `max(0, 100 - personTotalAllocationPercent)`. */
  availabilityPercent: z.number().int(),
  personStatus: resourcePersonStatusSchema,
});
export type ProjectAllocation = z.infer<typeof projectAllocationSchema>;

export const listProjectAllocationsQuerySchema = z.object({
  /** Off (default): only allocations still in force or starting later. On: ended rows too. */
  // 'true' | 'false' (not z.coerce.boolean(), which would read the string "false" as true).
  includeEnded: z.enum(['true', 'false']).optional(),
});

/** Bounded read: at most this many allocation rows are returned; `truncated` says more exist. */
export const PROJECT_ALLOCATIONS_MAX_ROWS = 200;

export const RESOURCE_FORECAST_MAX_WEEKS = 12;

export const getResourceForecastQuerySchema = z.object({
  /** How many weeks ahead, starting with the current (Monday-based) week. */
  weeks: z.coerce.number().int().min(1).max(RESOURCE_FORECAST_MAX_WEEKS).default(8),
  /** Keyset cursor: a personId, exactly like `listResourceAllocations`. */
  cursor: uuidSchema.optional(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
  personId: uuidSchema.optional(),
  departmentId: uuidSchema.optional(),
});

/** Logged hours may drift from allocated hours by this much (either way) before a row is flagged. */
export const PLANNED_VS_ACTUAL_TOLERANCE_PERCENT = 20;

/**
 * `within`: |logged - allocated| is inside the tolerance. `over` / `under`: logged is more / less
 * than the tolerance away from allocated. `unplanned`: hours logged with nothing allocated.
 * `no_capacity`: allocated, but the person has no working capacity in the window (no shift
 * schedule, all leave/holiday), so planned hours cannot be derived. `none`: nothing on either side.
 */
export const plannedVsActualFlagSchema = z.enum([
  'within',
  'under',
  'over',
  'unplanned',
  'no_capacity',
  'none',
]);
export type PlannedVsActualFlag = z.infer<typeof plannedVsActualFlagSchema>;

export const plannedVsActualSchema = z.object({
  /** Sum over the window of (the person's available working minutes that day x allocation %). */
  allocatedHours: z.string(),
  loggedHours: z.string(),
  /** `(logged - allocated) / allocated x 100`, one decimal; `null` when nothing is allocated. */
  deviationPercent: z.number().nullable(),
  flag: plannedVsActualFlagSchema,
});
export type PlannedVsActual = z.infer<typeof plannedVsActualSchema>;

export const resourceForecastPersonSchema = z.object({
  personId: uuidSchema,
  personName: z.string(),
  departmentName: z.string().nullable(),
  /** Aligned with `weekStarts`: the PEAK daily total allocation % within each week — a week that
   * is over 100% on any one day shows as over, which is what an over-allocation heatmap is for. */
  weeklyPercent: z.array(z.number().int()),
  /** Planned vs actual for this week so far (Monday to the caller's today), all projects. */
  currentWeek: plannedVsActualSchema,
});
export type ResourceForecastPerson = z.infer<typeof resourceForecastPersonSchema>;

export const resourceForecastSchema = z.object({
  /** Monday of each forecast week, `yyyy-mm-dd`. */
  weekStarts: z.array(dateOnlySchema),
  /** The window `currentWeek` was computed over. */
  currentWeekFrom: dateOnlySchema,
  currentWeekTo: dateOnlySchema,
  items: z.array(resourceForecastPersonSchema),
  nextCursor: z.string().nullable(),
});
export type ResourceForecast = z.infer<typeof resourceForecastSchema>;

// ---- Task dependencies / timeline (Phase 7) --------------------------------------------
//
// A dependency is a directed edge between two tasks of the SAME project: `taskId` is the dependent
// (it is "blocked by" `dependsOnTaskId`, the predecessor). A task may have several predecessors.
// The API stores edges only — whether a predecessor still blocks (e.g. it was cancelled) is derived
// client-side from the task's status BUCKET, never from a literal status string.
//
// Errors: self-link and cross-project links are 422; a duplicate edge or one that would close a
// cycle is 409; a task the caller cannot see (other org, or invisible in an isolated project) is 404.

export const taskDependencyEdgeSchema = z.object({
  taskId: uuidSchema,
  dependsOnTaskId: uuidSchema,
});
export type TaskDependencyEdge = z.infer<typeof taskDependencyEdgeSchema>;

export const setTaskDependencyBodySchema = z.object({
  dependsOnTaskId: uuidSchema,
});

export const resourceAllocationExclusionSchema = z.object({
  id: uuidSchema,
  departmentId: uuidSchema.nullable(),
  departmentName: z.string().nullable(),
  postId: uuidSchema.nullable(),
  postName: z.string().nullable(),
});
export type ResourceAllocationExclusion = z.infer<typeof resourceAllocationExclusionSchema>;

/** Exactly one of `departmentId`/`postId` — a row that excluded both dimensions at once wouldn't
 * mean anything sharper than two separate rows, and a row that excluded neither is meaningless. */
export const upsertAllocationExclusionBodySchema = z
  .object({
    departmentId: uuidSchema.optional(),
    postId: uuidSchema.optional(),
  })
  .superRefine((val, ctx) => {
    const count = Number(val.departmentId !== undefined) + Number(val.postId !== undefined);
    if (count !== 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Exactly one of departmentId or postId is required',
        path: count === 0 ? ['departmentId'] : ['postId'],
      });
    }
  });

// ---- Task review rejection (ADR 0020 §2) ---------------------------------------------

export const rejectTaskReviewBodySchema = z.object({
  reason: z.string().trim().min(1).max(500),
});

// ---- Task status definitions (ADR 0020 §6 — schema + CRUD only; see the ADR for what is
// deliberately deferred: `ProjectTask.status` itself still only accepts the six built-in values). ---

export const taskStatusBucketSchema = z.enum(TASK_STATUS_BUCKETS);

export const taskStatusDefinitionSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  key: z.string(),
  label: z.string(),
  bucket: taskStatusBucketSchema,
  color: z.string().nullable(),
  sortOrder: z.number().int(),
  isSystem: z.boolean(),
  isActive: z.boolean(),
});
export type TaskStatusDefinition = z.infer<typeof taskStatusDefinitionSchema>;

export const listTaskStatusDefinitionsQuerySchema = z.object({
  includeInactive: z.coerce.boolean().optional(),
});

export const createTaskStatusDefinitionBodySchema = z.object({
  key: z
    .string()
    .trim()
    .min(1)
    .max(50)
    .regex(/^[a-z][a-z0-9_]*$/, 'Use lowercase letters, digits and underscores, starting with a letter'),
  label: z.string().trim().min(1).max(100),
  bucket: taskStatusBucketSchema,
  color: z.string().trim().max(20).optional(),
  sortOrder: z.number().int().optional(),
});

export const updateTaskStatusDefinitionBodySchema = z.object({
  label: z.string().trim().min(1).max(100).optional(),
  bucket: taskStatusBucketSchema.optional(),
  color: z.string().trim().max(20).nullable().optional(),
  sortOrder: z.number().int().optional(),
  isActive: z.boolean().optional(),
});


// ---- Task comments ------------------------------------------------------------------

/** PM-008 (Keka gap closure, follow-up round 3) — one file attached to a comment (0-3 per
 * comment). Same shape as `expenseAttachmentSchema`: never carries the storage key, only ever
 * downloaded through `downloadTaskCommentAttachment`, which re-checks permission on every fetch. */
export const taskCommentAttachmentSchema = z.object({
  id: uuidSchema,
  taskCommentId: uuidSchema,
  fileName: z.string(),
  mimeType: z.string(),
  sizeBytes: z.number().int().nonnegative(),
  uploadedById: uuidSchema.nullable(),
  createdAt: instantSchema,
});
export type TaskCommentAttachment = z.infer<typeof taskCommentAttachmentSchema>;

export const taskCommentSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  taskId: uuidSchema,
  authorPersonId: uuidSchema,
  authorName: z.string(),
  body: z.string(),
  /** PM-008 — `@mention`ed person ids, re-validated server-side against who could actually see the
   * task at post time (never trusted as-is from the client — same rule chat.ts's own `mentions`
   * applies). Notified through the EXISTING `project.task_comment` key, recipient list extended;
   * no new notification key. */
  mentions: z.array(uuidSchema),
  attachments: z.array(taskCommentAttachmentSchema),
  createdAt: instantSchema,
});
export type TaskComment = z.infer<typeof taskCommentSchema>;

export const listTaskCommentsQuerySchema = paginationQuerySchema;

export const createTaskCommentBodySchema = z.object({
  body: z.string().trim().min(1).max(2000),
  /** Re-validated server-side (see taskCommentSchema's doc comment) — never trusted as-is. */
  mentions: z.array(uuidSchema).max(50).optional(),
});

export const MAX_TASK_COMMENT_ATTACHMENTS = 3;

/** Same base64 handshake as `uploadExpenseAttachmentBodySchema` (this codebase's one JSON-body
 * upload convention — see that schema's doc comment), enforced server-side against the SAME
 * allow-list and size cap (`ATTACHMENT_ALLOWED_TYPES`/`ATTACHMENT_MAX_BYTES`, storage.ts): PDF,
 * JPEG, PNG, 5 MB. */
export const uploadTaskCommentAttachmentBodySchema = z.object({
  fileName: z.string().trim().min(1).max(255),
  mimeType: z.string().trim().min(1).max(100),
  contentBase64: z.string().min(1),
});

export const downloadTaskCommentAttachmentResponseSchema = z.object({
  fileName: z.string(),
  mimeType: z.string(),
  contentBase64: z.string(),
});

// ---- Task reopen ----------------------------------------------------------------

export const reopenTaskBodySchema = z.object({
  reason: z.string().trim().min(1).max(500),
});

/**
 * Daily Work Planning T7.1 — unlike reopen/reject-review, a carry-forward's intent is obvious from
 * the action itself and `TaskCarryForwardRecord` has no reason/note column (see schema.prisma) —
 * no body fields at all, empty object kept (rather than no body) so the route still goes through
 * this file's usual `idempotency()`-friendly POST-with-body shape.
 */
export const carryForwardTaskBodySchema = z.object({});

/**
 * PM audit fix 8 — one row per carry-forward (`TaskCarryForwardRecord`, never duplicated against
 * the task's own row — see `carryForwardTask`'s header comment). `ProjectTask.carryForwardCount`
 * only ever surfaces the COUNT; this is the individual history (when, by whom) behind that number,
 * fetched lazily by `listTaskCarryForwardRecords` rather than inlined into every task response —
 * the count is already cheap via `_count`, but the full list is the kind of "a handful of rows,
 * only needed once someone actually asks" data this file keeps off the hot list/board path (same
 * reasoning as `TaskEvidence`/`TaskAdditionalAssignee` getting their own dedicated list routes
 * instead of living on `projectTaskSchema` directly).
 */
export const taskCarryForwardRecordSchema = z.object({
  id: uuidSchema,
  taskId: uuidSchema,
  carriedForwardByPersonId: uuidSchema,
  carriedForwardByName: z.string(),
  carriedForwardDate: dateOnlySchema,
  originalPlanDate: dateOnlySchema,
  createdAt: instantSchema,
});
export type TaskCarryForwardRecord = z.infer<typeof taskCarryForwardRecordSchema>;

// ---- Task evidence --------------------------------------------------------------

export const taskEvidenceKindSchema = z.enum(['image', 'video', 'document']);

export const taskEvidenceSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  taskId: uuidSchema,
  uploadedByPersonId: uuidSchema,
  uploadedByName: z.string(),
  kind: taskEvidenceKindSchema,
  fileName: z.string(),
  mimeType: z.string(),
  sizeBytes: z.number().int(),
  url: z.string(),
  note: z.string().nullable(),
  createdAt: instantSchema,
});
export type TaskEvidence = z.infer<typeof taskEvidenceSchema>;

export const requestTaskEvidenceUploadBodySchema = z.object({
  fileName: z.string().trim().min(1).max(255),
  contentType: z.string().trim().min(1).max(100),
  sizeBytes: z.number().int().positive(),
});
export const taskEvidenceUploadTicketSchema = z.object({
  uploadUrl: z.string(),
  attachmentUrl: z.string(),
  expiresIn: z.number().int(),
  kind: taskEvidenceKindSchema,
  storageKey: z.string(),
});

export const createTaskEvidenceBodySchema = z.object({
  storageKey: z.string().trim().min(1).max(1000),
  attachmentUrl: z.string().trim().min(1).max(2000),
  kind: taskEvidenceKindSchema,
  fileName: z.string().trim().min(1).max(255),
  mimeType: z.string().trim().min(1).max(100),
  sizeBytes: z.number().int().positive(),
  note: z.string().trim().max(500).optional(),
});

// ---- Task extension requests ------------------------------------------------------

export const taskExtensionStatusSchema = z.enum(['pending', 'approved', 'rejected']);

export const taskExtensionRequestSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  taskId: uuidSchema,
  taskTitle: z.string(),
  requestedByPersonId: uuidSchema,
  requestedByName: z.string(),
  currentDueDate: dateOnlySchema.nullable(),
  requestedDueDate: dateOnlySchema,
  reason: z.string(),
  status: taskExtensionStatusSchema,
  decidedByPersonId: uuidSchema.nullable(),
  decidedAt: instantSchema.nullable(),
  decisionNote: z.string().nullable(),
  createdAt: instantSchema,
});
export type TaskExtensionRequest = z.infer<typeof taskExtensionRequestSchema>;

export const listTaskExtensionRequestsQuerySchema = paginationQuerySchema.extend({
  taskId: uuidSchema.optional(),
  status: taskExtensionStatusSchema.optional(),
  /** Requests waiting for the caller's decision, across their reachable tasks — the Extension Requests queue. */
  scope: z.enum(['mine', 'team']).optional(),
});

export const requestTaskExtensionBodySchema = z.object({
  requestedDueDate: dateOnlySchema,
  reason: z.string().trim().min(1).max(500),
});

export const decideTaskExtensionBodySchema = z.object({
  action: z.enum(['approve', 'reject']),
  note: z.string().trim().max(500).optional(),
});

// ---- Task reminder snooze ---------------------------------------------------------

export const snoozeTaskReminderBodySchema = z.object({
  /** Minutes from now; capped so "remind me later" cannot silently mean "never". */
  minutes: z.number().int().min(5).max(1440),
});

// ---- KPIs -------------------------------------------------------------------------

export const kpiPeriodSchema = z.enum(['weekly', 'monthly', 'quarterly', 'annual']);

export const kpiSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  /** T0.12: the KPI's own legal entity, or `null` for a legacy/unscoped KPI (see the doc comment on
   * `periodStart`/`periodEnd` below). Drives which timezone and fiscalYearStartMonth its progress
   * period resolves against — never an org-wide "default legal entity", which does not exist here. */
  legalEntityId: uuidSchema.nullable(),
  code: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  unit: z.string(),
  targetValue: decimalStringSchema,
  period: kpiPeriodSchema,
  isActive: z.boolean(),
  /** Progress within the CURRENT period, computed from linked tasks completed inside it. Never stored.
   * "Current" resolves in `legalEntityId`'s own timezone/fiscal year when set, else the server-UTC
   * approximation. */
  currentValue: decimalStringSchema,
  periodStart: dateOnlySchema,
  periodEnd: dateOnlySchema,
});
export type Kpi = z.infer<typeof kpiSchema>;

export const listKpisQuerySchema = z.object({
  includeInactive: z.coerce.boolean().optional(),
});

export const createKpiBodySchema = z.object({
  /** Optional — when omitted and the org has exactly one legal entity, the API auto-assigns it.
   * A multi-entity org may still create a KPI with no legal entity (left on the UTC approximation). */
  legalEntityId: uuidSchema.optional(),
  code: z
    .string()
    .trim()
    .min(1)
    .max(50)
    .regex(/^[A-Za-z0-9_-]+$/, 'Use letters, digits, underscore or hyphen'),
  name: z.string().trim().min(1).max(100),
  description: z.string().trim().max(1000).optional(),
  unit: z.string().trim().min(1).max(30).default('count'),
  targetValue: decimalStringSchema,
  period: kpiPeriodSchema.default('monthly'),
});

export const updateKpiBodySchema = z.object({
  legalEntityId: uuidSchema.nullable().optional(),
  name: z.string().trim().min(1).max(100).optional(),
  description: z.string().trim().max(1000).nullable().optional(),
  unit: z.string().trim().min(1).max(30).optional(),
  targetValue: decimalStringSchema.optional(),
  period: kpiPeriodSchema.optional(),
  isActive: z.boolean().optional(),
});

// ---- Employee Targets (Daily Work Planning Phase 4) --------------------------------

/** A positive decimal string — `targetValue` must be > 0 (plan T4.2), unlike `Kpi.targetValue`
 * (which only disallows negative). Local, same convention as this file's `nonNegativeDecimalSchema`. */
const positiveDecimalSchema = decimalStringSchema.refine(
  (v) => Number.parseFloat(v) > 0,
  'Must be greater than 0',
);

/**
 * "Did THIS employee hit THEIR number" — distinct from, and measured against, the org-wide `Kpi`
 * (reused verbatim, never forked: see `Kpi`'s own schema.prisma doc comment and the approved
 * decision "do not convert org-level KPI into employee-specific"). Different employees may hold
 * different targets against the SAME `kpiId` — no uniqueness constraint. `achievedValue`/
 * `remainingValue`/`achievementPercent` are computed on READ from linked `ProjectTask.achievedValue`
 * contributions (same "never a second source of truth" pattern `Kpi.currentValue` already uses) —
 * never stored, never sent as an input on create/update.
 */
export const employeeTargetSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  personId: uuidSchema,
  personName: z.string(),
  kpiId: uuidSchema,
  kpiName: z.string(),
  targetValue: positiveDecimalSchema,
  unit: z.string(),
  /** Fully custom per target, independent of the parent Kpi's own `period` cadence (plan Decision 4). */
  startDate: dateOnlySchema,
  endDate: dateOnlySchema,
  /** Summed from every `ProjectTask.achievedValue` explicitly linked via `employeeTargetId`,
   * completed-bucket only, within [`startDate`,`endDate`] — see `computeEmployeeTargetAchievement`. */
  achievedValue: decimalStringSchema,
  /** `targetValue - achievedValue`, derived — never stored, can go negative when over-achieved. */
  remainingValue: decimalStringSchema,
  /** `achievedValue / targetValue * 100`, derived — never stored, not capped at 100. */
  achievementPercent: decimalStringSchema,
  createdAt: instantSchema,
  updatedAt: instantSchema,
});
export type EmployeeTarget = z.infer<typeof employeeTargetSchema>;

export const listEmployeeTargetsQuerySchema = z.object({
  /** Unset = every target the caller's roster reaches (self + reports, or org-wide for an
   * `org.settings:update` holder) — see the route's doc comment for the exact resolution. */
  personId: uuidSchema.optional(),
});

export const createEmployeeTargetBodySchema = z
  .object({
    personId: uuidSchema,
    kpiId: uuidSchema,
    targetValue: positiveDecimalSchema,
    unit: z.string().trim().min(1).max(40),
    startDate: dateOnlySchema,
    endDate: dateOnlySchema,
  })
  .refine((body) => body.endDate >= body.startDate, {
    message: 'endDate must be on or after startDate',
    path: ['endDate'],
  });

/**
 * Every field optional (a partial update); `personId`/`kpiId` are NOT editable after creation —
 * same "identity fields are set once" posture as other assignment-shaped records in this file
 * (e.g. `ProjectMember.personId`). The route re-validates `endDate >= startDate` against the
 * EFFECTIVE merged state (existing row + this patch), same division of labor `updateTask`'s own
 * `targetValue`/`unit` cross-check already establishes — a schema alone cannot see the existing row.
 */
export const updateEmployeeTargetBodySchema = z.object({
  targetValue: positiveDecimalSchema.optional(),
  unit: z.string().trim().min(1).max(40).optional(),
  startDate: dateOnlySchema.optional(),
  endDate: dateOnlySchema.optional(),
});

// ---- Next-day planning (Daily Work Planning Phase 8) --------------------------------

/**
 * Read-only reflection of a `DailyPerformanceSnapshot` row — the SAME shape the row already has
 * (see `@hrms/db`'s `daily-performance-snapshot.ts`), not recomputed here. `null` when the person
 * has no snapshot for that date (never checked out and the worker catch-up sweep hasn't run yet —
 * see apps/worker's `sweepDailyPerformanceSnapshotCatchup`, 23:00 local).
 */
export const nextDayPlanSnapshotSchema = z.object({
  date: dateOnlySchema,
  assignedCount: z.number().int().nonnegative(),
  selfAddedCount: z.number().int().nonnegative(),
  carriedForwardCount: z.number().int().nonnegative(),
  completedCount: z.number().int().nonnegative(),
  partialCount: z.number().int().nonnegative(),
  pendingCount: z.number().int().nonnegative(),
  completionPercent: z.number(),
  plannedMinutes: z.number().int().nonnegative(),
  actualMinutes: z.number().int().nonnegative(),
  targetAchievementSummary: z.array(
    z.object({
      employeeTargetId: uuidSchema,
      kpiName: z.string(),
      targetValue: decimalStringSchema,
      achievedValue: decimalStringSchema,
      unit: z.string(),
    }),
  ),
  /** `checkout` (the person's own submission) or `worker_catchup` (the EOD sweep's best-effort
   * guess for someone who never checked out) — see the column's own schema.prisma doc comment. */
  source: z.enum(['checkout', 'worker_catchup']),
});
/** Named export (Phase 9 / T9.1) — `getDailyPerformance`/`getPerformanceTrend` and their web
 * components (`DailyPerformanceSummary`, `PerformanceTrendPage`) use this directly rather than each
 * re-inferring the same zod shape. */
export type PerformanceSnapshot = z.infer<typeof nextDayPlanSnapshotSchema>;

/**
 * One person's slice of the manager planning view: yesterday's numbers (read directly, never
 * recomputed), today's carry-forward candidates (the SAME open/due-today-or-earlier query the
 * Carry-Forward UI itself uses), and every `EmployeeTarget` active on the planning date with its
 * live achievement. Composition only — carrying forward or creating a tomorrow task still goes
 * through `carryForwardTask`/`createTask`, not a new write surface here.
 */
export const nextDayPlanPersonSchema = z.object({
  personId: uuidSchema,
  personName: z.string(),
  yesterday: nextDayPlanSnapshotSchema.nullable(),
  carryForwardCandidates: z.array(projectTaskSchema),
  activeTargets: z.array(employeeTargetSchema),
});
export type NextDayPlanPerson = z.infer<typeof nextDayPlanPersonSchema>;

export const getNextDayPlanQuerySchema = z.object({
  /** The planning day — defaults to the caller's own "today" (their legal entity's zone when one
   * resolves, else UTC) when omitted. "Yesterday" (this date minus one day) is where the
   * performance snapshot is read from. */
  date: dateOnlySchema.optional(),
  /** Narrow to one person within the caller's roster. Outside the roster: answered the same as "not
   * found in scope" (an empty `people` array), never a 403 that would confirm the id exists — same
   * posture `listEmployeeTargets`/`listResourceAllocations` already use for a roster-scoped read. */
  personId: uuidSchema.optional(),
});

export const nextDayPlanResponseSchema = z.object({
  /** The planning date itself (today, or the explicit `?date=`). */
  date: dateOnlySchema,
  /** `date` minus one day — where `yesterday` on each person is read from. */
  yesterdayDate: dateOnlySchema,
  people: z.array(nextDayPlanPersonSchema),
});

// ---- Performance analytics (Daily Work Planning Phase 9) ---------------------------
//
// Read-only views over `DailyPerformanceSnapshot` for Employee ("my performance"), Manager
// (reporting-tree) and HR/Org Owner (org-wide). The per-day shape is the SAME
// `nextDayPlanSnapshotSchema` Phase 8 already defined for `getNextDayPlan`'s own `yesterday` field
// — one schema for "a day's snapshot, on the wire", not a second near-duplicate.

export const getDailyPerformanceQuerySchema = z.object({
  /** Defaults to the SUBJECT person's own "today" (their legal entity's zone when one resolves,
   * else UTC) when omitted — CLAUDE.md invariant 3: "what day is it for this employee" is always
   * resolved in the subject's own terms, not the caller's. */
  date: dateOnlySchema.optional(),
});

export const getPerformanceTrendQuerySchema = z.object({
  /** Both optional — see `resolvePerformanceTrendRange` (apps/api/src/lib/project-planning.ts) for
   * the exact default (30-day trailing window) and the 90-day cap. */
  from: dateOnlySchema.optional(),
  to: dateOnlySchema.optional(),
});

export const performanceTrendResponseSchema = z.object({
  items: z.array(nextDayPlanSnapshotSchema),
});

// ---- Task reminder schedules (company-configurable) --------------------------------

export const taskReminderSlotSchema = z.enum(['morning', 'evening']);

export const taskReminderScheduleSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  legalEntityId: uuidSchema.nullable(),
  slot: taskReminderSlotSchema,
  enabled: z.boolean(),
  localTime: z.string(),
  popup: z.boolean(),
  kpiOnly: z.boolean(),
});
export type TaskReminderSchedule = z.infer<typeof taskReminderScheduleSchema>;

const HHMM = /^([01]\d|2[0-3]):(00|15|30|45)$/;

export const listTaskReminderSchedulesQuerySchema = z.object({
  legalEntityId: legalEntityIdSchema.optional(),
});

export const upsertTaskReminderScheduleBodySchema = z.object({
  legalEntityId: uuidSchema.nullable().optional(),
  slot: taskReminderSlotSchema,
  enabled: z.boolean().default(true),
  localTime: z.string().regex(HHMM, 'Use HH:MM, a quarter-hour, e.g. 09:00 or 18:30'),
  popup: z.boolean().default(false),
  kpiOnly: z.boolean().default(false),
});

// ---- Analytics -------------------------------------------------------------------

export const getProjectAnalyticsQuerySchema = z.object({
  from: dateOnlySchema.optional(),
  to: dateOnlySchema.optional(),
});

// ---- Project time report (PM-016) ---------------------------------------------------------
//
// Hours by person / task / day for ONE project, for the people managing it. HOURS ONLY (no rate,
// no money). Entries come from `TimeEntry` (the timesheet lines); a REJECTED timesheet's entries are
// left out of every figure (and counted once in `totals.rejectedHoursExcluded`), everything else is
// reported and split by approval state instead of being silently filtered:
//   approved = the timesheet is approved or locked, pending = submitted and awaiting a decision,
//   draft    = not yet submitted (a draft timesheet, or an entry not on any timesheet yet).
// Dates are date-only; the default window ends on "today" in the project's legal-entity timezone.

export const PROJECT_TIME_REPORT_MAX_DAYS = 366;
/** Person/task groupings return at most this many rows by default (top-N by hours, the rest folded into one `other` row). */
export const PROJECT_TIME_REPORT_DEFAULT_LIMIT = 50;

export const projectTimeReportGroupBySchema = z.enum(['person', 'task', 'day']);
export type ProjectTimeReportGroupBy = z.infer<typeof projectTimeReportGroupBySchema>;

export const getProjectTimeReportQuerySchema = z
  .object({
    /** Inclusive. Defaults to 29 days before `to`. */
    from: dateOnlySchema.optional(),
    /** Inclusive. Defaults to today in the project's legal-entity timezone. */
    to: dateOnlySchema.optional(),
    groupBy: projectTimeReportGroupBySchema.default('person'),
    /** Row cap for `person` / `task` (top-N + `other`). `day` is bounded by the date range instead. */
    limit: z.coerce.number().int().min(1).max(100).default(PROJECT_TIME_REPORT_DEFAULT_LIMIT),
  })
  .superRefine((val, ctx) => {
    if (val.from && val.to) {
      if (val.from > val.to) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'from cannot be after to',
          path: ['from'],
        });
      } else {
        const days =
          (Date.parse(`${val.to}T00:00:00Z`) - Date.parse(`${val.from}T00:00:00Z`)) / 86_400_000 + 1;
        if (days > PROJECT_TIME_REPORT_MAX_DAYS) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `The range can span at most ${PROJECT_TIME_REPORT_MAX_DAYS} days`,
            path: ['to'],
          });
        }
      }
    }
  });

/** Hour figures are decimal strings with two places (`"12.50"`). */
export const projectTimeHoursSchema = z.object({
  totalHours: z.string(),
  billableHours: z.string(),
  approvedHours: z.string(),
  pendingHours: z.string(),
  draftHours: z.string(),
  entryCount: z.number().int().nonnegative(),
});
export type ProjectTimeHours = z.infer<typeof projectTimeHoursSchema>;

/**
 * `item`: a real person / task / day. `other`: the rows beyond the limit, folded together.
 * `no_task`: time logged on the project without a task. `restricted`: tasks the caller may not see
 * on an isolated project — folded into one row so no title or id leaks.
 */
export const projectTimeRowKindSchema = z.enum(['item', 'other', 'no_task', 'restricted']);

export const projectTimeReportRowSchema = projectTimeHoursSchema.extend({
  /** personId | taskId | yyyy-mm-dd, or a fixed key for a non-`item` row. */
  key: z.string(),
  label: z.string(),
  kind: projectTimeRowKindSchema,
  /** Person grouping only: hours allocated to this project (capacity x allocation %) vs logged,
   * compared up to `comparedThrough`. `null` on other groupings and on the `other` row. */
  plannedVsActual: plannedVsActualSchema.nullable(),
});
export type ProjectTimeReportRow = z.infer<typeof projectTimeReportRowSchema>;

export const projectTimeReportSchema = z.object({
  projectId: uuidSchema,
  from: dateOnlySchema,
  to: dateOnlySchema,
  groupBy: projectTimeReportGroupBySchema,
  /** IANA zone the default window / "today" was resolved in; `null` for a project with no legal entity (UTC). */
  timezone: z.string().nullable(),
  /** Planned-vs-actual is compared over `from`..`comparedThrough` (never beyond today); `null` when the whole range is in the future. */
  comparedThrough: dateOnlySchema.nullable(),
  /** True when the caller only sees hours of people inside their reach (a manager's team), not the whole project. */
  narrowedToRoster: z.boolean(),
  totals: projectTimeHoursSchema.extend({
    /** Hours on rejected timesheets, excluded from every other figure. */
    rejectedHoursExcluded: z.string(),
  }),
  rows: z.array(projectTimeReportRowSchema),
  /** How many groups exist before the row cap; `rows.length` is smaller when `truncated`. */
  groupCount: z.number().int().nonnegative(),
  truncated: z.boolean(),
});
export type ProjectTimeReport = z.infer<typeof projectTimeReportSchema>;

// ---------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------

export const projectContract = c.router(
  {
    // ---- Projects -------------------------------------------------------
    listProjects: {
      method: 'GET',
      path: '/',
      query: listProjectsQuerySchema,
      responses: { 200: paginatedSchema(projectSchema), 401: errorBodySchema },
      metadata: routeMeta('project:read'),
      summary: "List projects in the caller's visible scope",
    },
    createProject: {
      method: 'POST',
      path: '/',
      body: createProjectBodySchema,
      responses: {
        201: projectSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        409: errorBodySchema,
        422: errorBodySchema,
      },
      metadata: routeMeta('project:create'),
      summary: 'Create a project',
    },
    updateProject: {
      method: 'PATCH',
      path: '/:projectId',
      body: updateProjectBodySchema,
      responses: {
        200: projectSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        422: errorBodySchema,
      },
      metadata: routeMeta('project:update'),
      summary: 'Update a project',
    },
    archiveProject: {
      method: 'POST',
      path: '/:projectId/archive',
      body: c.noBody(),
      responses: {
        200: projectSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      metadata: routeMeta('project:archive'),
      summary: 'Archive a project',
    },

    // ---- Members ----------------------------------------------------------
    listMembers: {
      method: 'GET',
      path: '/:projectId/members',
      query: listMembersQuerySchema,
      responses: {
        200: paginatedSchema(projectMemberSchema),
        401: errorBodySchema,
        404: errorBodySchema,
      },
      metadata: routeMeta('project:read'),
      summary: "List a project's members",
    },
    addMember: {
      method: 'POST',
      path: '/:projectId/members',
      body: addMemberBodySchema,
      responses: {
        201: projectMemberSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      metadata: routeMeta('project.member:manage'),
      summary: 'Add a member to a project',
    },
    addMembersBulk: {
      method: 'POST',
      path: '/:projectId/members/bulk',
      body: addMembersBulkBodySchema,
      responses: {
        201: addMembersBulkResponseSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      metadata: routeMeta('project.member:manage'),
      summary: 'Add several people to a project at once, same role and start date',
    },
    endMember: {
      method: 'POST',
      path: '/:projectId/members/:memberId/end',
      body: endMemberBodySchema,
      responses: {
        200: projectMemberSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      metadata: routeMeta('project.member:manage'),
      summary: "End a member's project membership as of validTo",
    },

    // ---- Milestones ---------------------------------------------------------
    listMilestones: {
      method: 'GET',
      path: '/:projectId/milestones',
      query: listMilestonesQuerySchema,
      responses: {
        200: paginatedSchema(projectMilestoneSchema),
        401: errorBodySchema,
        404: errorBodySchema,
      },
      metadata: routeMeta('project:read'),
      summary: "List a project's milestones",
    },
    createMilestone: {
      method: 'POST',
      path: '/:projectId/milestones',
      body: createMilestoneBodySchema,
      responses: {
        201: projectMilestoneSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      metadata: routeMeta('project.milestone:manage'),
      summary: 'Create a milestone',
    },
    updateMilestone: {
      method: 'PATCH',
      path: '/:projectId/milestones/:milestoneId',
      body: updateProjectMilestoneBodySchema,
      responses: {
        200: projectMilestoneSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      metadata: routeMeta('project.milestone:manage'),
      summary: 'Update a milestone',
    },
    deleteMilestone: {
      method: 'DELETE',
      path: '/:projectId/milestones/:milestoneId',
      body: c.type<undefined>(),
      responses: {
        200: deleteResultSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** At least one task still references this milestone. */
        409: errorBodySchema,
      },
      metadata: routeMeta('project.milestone:manage'),
      summary: 'Delete a milestone — refused while any task still references it',
    },

    // ---- Phases (PM-005) ------------------------------------------------------
    listPhases: {
      method: 'GET',
      path: '/:projectId/phases',
      query: listPhasesQuerySchema,
      responses: {
        200: paginatedSchema(projectPhaseSchema),
        401: errorBodySchema,
        404: errorBodySchema,
      },
      // Same visibility as listMilestones — a plain project read, not a manage-only screen.
      metadata: routeMeta('project:read'),
      summary: "List a project's phases",
    },
    createPhase: {
      method: 'POST',
      path: '/:projectId/phases',
      body: createPhaseBodySchema,
      responses: {
        201: projectPhaseSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      // Reuses project.milestone:manage (PM-005: "phases are structurally similar to milestones") —
      // this permission already gates exactly this shape of write (create/update a project-scoped
      // grouping), so no new permission string.
      metadata: routeMeta('project.milestone:manage'),
      summary: 'Create a phase',
    },
    updatePhase: {
      method: 'PATCH',
      path: '/:projectId/phases/:phaseId',
      body: updatePhaseBodySchema,
      responses: {
        200: projectPhaseSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      metadata: routeMeta('project.milestone:manage'),
      summary: 'Update a phase',
    },
    deletePhase: {
      method: 'DELETE',
      path: '/:projectId/phases/:phaseId',
      body: c.type<undefined>(),
      responses: {
        200: deleteResultSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      metadata: routeMeta('project.milestone:manage'),
      summary: 'Delete a phase — refused while any task still references it',
    },

    // ---- Tasks --------------------------------------------------------------
    listTasks: {
      method: 'GET',
      path: '/tasks',
      query: listTasksQuerySchema,
      responses: { 200: paginatedSchema(projectTaskSchema), 401: errorBodySchema },
      metadata: routeMeta('project.task:read'),
      summary:
        'List tasks, optionally filtered to one project or one assignee — the same endpoint ' +
        "backs both a single project's board and the cross-project My Tasks board",
    },
    createTask: {
      method: 'POST',
      path: '/tasks',
      body: createTaskBodySchema,
      responses: {
        201: projectTaskSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** PM audit fix 4 — the parent project is archived or cancelled. */
        409: errorBodySchema,
      },
      metadata: routeMeta('project.task:manage'),
      summary: 'Create a task on a project',
    },
    updateTask: {
      method: 'PATCH',
      path: '/tasks/:taskId',
      body: updateTaskBodySchema,
      responses: {
        200: projectTaskSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        /** The handler's own effective-merged-state checks (unit-required, PM audit fix 1's
         * dueDate/startDate order) — pre-existing drift the unit check already had; declared here
         * alongside fix 1's new check rather than left undeclared a second time. */
        422: errorBodySchema,
      },
      metadata: routeMeta(['project.task:manage', 'project.task:read']),
      summary:
        'Update a task — the assignee may PATCH only {status} on their own task without ' +
        'project.task:manage',
    },
    deleteTask: {
      method: 'DELETE',
      path: '/tasks/:taskId',
      body: c.type<undefined>(),
      responses: {
        200: deleteResultSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** Has sub-tasks, has a dependency edge either direction, or is not in the not-started or
         * cancelled bucket — use Cancel for an in-progress/completed task instead. */
        409: errorBodySchema,
      },
      metadata: routeMeta('project.task:manage'),
      summary:
        'Delete a task — only when it has no sub-tasks or dependency edges, and is not-started or cancelled',
    },

    // ---- Additional assignees (PM-033) ---------------------------------------
    listTaskAssignees: {
      method: 'GET',
      path: '/tasks/:taskId/assignees',
      responses: {
        200: z.object({ items: z.array(taskAdditionalAssigneeSchema) }),
        401: errorBodySchema,
        404: errorBodySchema,
      },
      // Same visibility as reading the task itself.
      metadata: routeMeta('project.task:read'),
      summary: "A task's secondary assignees",
    },
    addTaskAssignee: {
      method: 'POST',
      path: '/tasks/:taskId/assignees',
      body: addTaskAssigneeBodySchema,
      responses: {
        201: taskAdditionalAssigneeSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      // Reuses project.task:manage — the same permission reassigning the primary assignee already
      // needs, and the same must-be-a-project-member check.
      metadata: routeMeta('project.task:manage'),
      summary: 'Add a secondary assignee to a task',
    },
    removeTaskAssignee: {
      method: 'DELETE',
      path: '/tasks/:taskId/assignees/:personId',
      body: c.type<undefined>(),
      responses: {
        200: deleteResultSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      metadata: routeMeta('project.task:manage'),
      summary: 'Remove a secondary assignee from a task',
    },

    // ---- Task types ---------------------------------------------------------
    listTaskTypes: {
      method: 'GET',
      path: '/task-types',
      query: listTaskTypesQuerySchema,
      responses: { 200: z.object({ items: z.array(taskTypeSchema) }), 401: errorBodySchema },
      metadata: routeMeta('project.task:read'),
      summary: "The organisation's task types (active ones unless includeInactive)",
    },
    createTaskType: {
      method: 'POST',
      path: '/task-types',
      body: createTaskTypeBodySchema,
      responses: {
        201: taskTypeSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        409: errorBodySchema,
      },
      metadata: routeMeta('org.settings:update'),
      summary: 'Create a task type',
    },
    updateTaskType: {
      method: 'PATCH',
      path: '/task-types/:taskTypeId',
      body: updateTaskTypeBodySchema,
      responses: {
        200: taskTypeSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      metadata: routeMeta('org.settings:update'),
      summary: 'Rename, reorder, activate or deactivate a task type',
    },

    // ---- Task templates (Phase 5) -------------------------------------------------
    listTaskTemplates: {
      method: 'GET',
      path: '/task-templates',
      responses: { 200: z.object({ items: z.array(taskTemplateSchema) }), 401: errorBodySchema, 403: errorBodySchema },
      // Readable by anyone who can APPLY a template (project.task:manage) — the apply bar needs the
      // list. Creating/editing templates stays org.settings:update (org-wide configuration).
      metadata: routeMeta('project.task:manage'),
      summary: "The organisation's task templates (item count only — GET one for its items)",
    },
    getTaskTemplate: {
      method: 'GET',
      path: '/task-templates/:templateId',
      responses: { 200: taskTemplateWithItemsSchema, 401: errorBodySchema, 403: errorBodySchema, 404: errorBodySchema },
      metadata: routeMeta('project.task:manage'),
      summary: 'One task template with its full item list',
    },
    createTaskTemplate: {
      method: 'POST',
      path: '/task-templates',
      body: createProjectTaskTemplateBodySchema,
      responses: {
        201: taskTemplateWithItemsSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        409: errorBodySchema,
        422: errorBodySchema,
      },
      metadata: routeMeta('org.settings:update'),
      summary: 'Create a reusable task template with its item set',
    },
    updateTaskTemplate: {
      method: 'PATCH',
      path: '/task-templates/:templateId',
      body: updateProjectTaskTemplateBodySchema,
      responses: {
        200: taskTemplateWithItemsSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        422: errorBodySchema,
      },
      metadata: routeMeta('org.settings:update'),
      summary: 'Rename a template and/or replace its whole item set',
    },
    applyTaskTemplate: {
      method: 'POST',
      path: '/task-templates/:templateId/apply',
      body: applyProjectTaskTemplateBodySchema,
      responses: {
        201: applyTaskTemplateResponseSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      metadata: routeMeta('project.task:manage'),
      summary:
        'Create one ProjectTask per template item on a project; items whose TaskType/Kpi has ' +
        'since been deactivated are skipped individually, never a hard failure of the whole apply',
    },

    // ---- Bulk task import (Phase 5) -------------------------------------------------
    importTasksBulk: {
      method: 'POST',
      path: '/tasks/bulk-import',
      body: importTasksBulkBodySchema,
      responses: {
        201: importTasksBulkResponseSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      // ai: 'hidden' — the body is up to 500 parsed CSV rows, which is not something a chat model can
      // meaningfully author (same "unsatisfiable request" reason as the document-parse routes); the
      // copilot reaches it through the import screen, not a derived tool. applyTaskTemplate and the
      // other PM writes stay AI-visible: their writes are proposals executed on the person's click.
      metadata: routeMeta('project.task:manage', { ai: 'hidden' }),
      summary:
        'Create tasks on a project from parsed CSV rows; bad rows are skipped and reported ' +
        'individually, good rows still import',
    },

    // ---- Task status definitions (ADR 0020 §6) -----------------------------------
    listTaskStatusDefinitions: {
      method: 'GET',
      path: '/task-statuses',
      query: listTaskStatusDefinitionsQuerySchema,
      responses: { 200: z.object({ items: z.array(taskStatusDefinitionSchema) }), 401: errorBodySchema },
      metadata: routeMeta('project.task:read'),
      summary: "The organisation's task status definitions (the six built-ins plus any custom ones)",
    },
    createTaskStatusDefinition: {
      method: 'POST',
      path: '/task-statuses',
      body: createTaskStatusDefinitionBodySchema,
      responses: { 201: taskStatusDefinitionSchema, 401: errorBodySchema, 403: errorBodySchema, 409: errorBodySchema },
      metadata: routeMeta('org.settings:update'),
      summary: 'Define a custom task status, mapped onto one of the four buckets',
    },
    updateTaskStatusDefinition: {
      method: 'PATCH',
      path: '/task-statuses/:statusId',
      body: updateTaskStatusDefinitionBodySchema,
      responses: { 200: taskStatusDefinitionSchema, 401: errorBodySchema, 403: errorBodySchema, 404: errorBodySchema, 409: errorBodySchema },
      metadata: routeMeta('org.settings:update'),
      summary: "Rename, recolour, reorder, rebucket or deactivate a status; a built-in's bucket is fixed",
    },

    // ---- Task comments --------------------------------------------------------
    listTaskComments: {
      method: 'GET',
      path: '/tasks/:taskId/comments',
      query: listTaskCommentsQuerySchema,
      responses: { 200: paginatedSchema(taskCommentSchema), 401: errorBodySchema, 403: errorBodySchema, 404: errorBodySchema },
      metadata: routeMeta('project.task:read'),
      summary: 'Comments on a task, in the caller visible scope',
    },
    createTaskComment: {
      method: 'POST',
      path: '/tasks/:taskId/comments',
      body: createTaskCommentBodySchema,
      responses: { 201: taskCommentSchema, 401: errorBodySchema, 403: errorBodySchema, 404: errorBodySchema },
      metadata: routeMeta('project.task:read'),
      summary: 'Comment on a task the caller can read',
    },
    uploadTaskCommentAttachment: {
      method: 'POST',
      path: '/tasks/:taskId/comments/:commentId/attachments',
      body: uploadTaskCommentAttachmentBodySchema,
      responses: {
        201: taskCommentAttachmentSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** Already at MAX_TASK_COMMENT_ATTACHMENTS, or the caller is not the comment's author. */
        409: errorBodySchema,
        /** Rejected type, empty/invalid base64, or over the size limit. */
        422: errorBodySchema,
      },
      metadata: routeMeta('project.task:read', { ai: 'hidden' }),
      // ai-hidden for the same reason uploadClaimAttachment is: the REQUEST carries file bytes as
      // base64, which a model cannot satisfy without hallucinating content.
      summary: 'Attach a file to one of the caller\'s own task comments (max 3 per comment)',
    },
    downloadTaskCommentAttachment: {
      method: 'GET',
      path: '/tasks/:taskId/comments/:commentId/attachments/:attachmentId',
      responses: {
        200: downloadTaskCommentAttachmentResponseSchema,
        401: errorBodySchema,
        404: errorBodySchema,
      },
      // ai-hidden: the RESPONSE carries base64 file bytes, unusable/wasteful in a model's context.
      metadata: routeMeta('project.task:read', { ai: 'hidden' }),
      summary: 'Download one task comment attachment',
    },

    // ---- Task reopen ------------------------------------------------------------
    reopenTask: {
      method: 'POST',
      path: '/tasks/:taskId/reopen',
      body: reopenTaskBodySchema,
      responses: { 200: projectTaskSchema, 401: errorBodySchema, 403: errorBodySchema, 404: errorBodySchema, 409: errorBodySchema },
      metadata: routeMeta('project.task:manage'),
      summary: 'Reopen a done or cancelled task, with a reason; notifies the assignee',
    },

    // ---- Task review rejection (ADR 0020 §2) -------------------------------------
    rejectTaskReview: {
      method: 'POST',
      path: '/tasks/:taskId/reject-review',
      body: rejectTaskReviewBodySchema,
      responses: { 200: projectTaskSchema, 401: errorBodySchema, 403: errorBodySchema, 404: errorBodySchema, 409: errorBodySchema },
      metadata: routeMeta('project.task:manage'),
      summary: "Send a task back from review to in-progress, with a reason; notifies the assignee",
    },

    // ---- Task carry-forward (Daily Work Planning Phase 7) ------------------------
    carryForwardTask: {
      method: 'POST',
      path: '/tasks/:taskId/carry-forward',
      body: carryForwardTaskBodySchema,
      responses: {
        200: projectTaskSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** Already in the completed bucket — carrying forward a done task makes no sense. */
        409: errorBodySchema,
      },
      metadata: routeMeta('project.task:manage'),
      summary:
        'Carry a still-open task forward to today: moves dueDate to today, sets originKind to ' +
        "carried_forward, and records one TaskCarryForwardRecord row of history. The SAME task " +
        'row is reused — never a duplicate.',
    },
    // PM audit fix 8 — the individual records behind `ProjectTask.carryForwardCount`. Same
    // visibility as reading the task itself, same shape as `listTaskEvidence`/`listTaskAssignees`.
    listTaskCarryForwardRecords: {
      method: 'GET',
      path: '/tasks/:taskId/carry-forward-records',
      responses: {
        200: z.object({ items: z.array(taskCarryForwardRecordSchema) }),
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      metadata: routeMeta('project.task:read'),
      summary: "A task's individual carry-forward history records (date carried, who carried it)",
    },

    // ---- Task evidence ----------------------------------------------------------
    requestTaskEvidenceUpload: {
      method: 'POST',
      path: '/tasks/:taskId/evidence/upload-url',
      body: requestTaskEvidenceUploadBodySchema,
      responses: { 201: taskEvidenceUploadTicketSchema, 401: errorBodySchema, 403: errorBodySchema, 404: errorBodySchema, 422: errorBodySchema },
      metadata: routeMeta('project.task:read'),
      summary: 'A presigned URL to upload one piece of completion evidence',
    },
    listTaskEvidence: {
      method: 'GET',
      path: '/tasks/:taskId/evidence',
      responses: { 200: z.object({ items: z.array(taskEvidenceSchema) }), 401: errorBodySchema, 403: errorBodySchema, 404: errorBodySchema },
      metadata: routeMeta('project.task:read'),
      summary: 'Evidence attached to a task',
    },
    createTaskEvidence: {
      method: 'POST',
      path: '/tasks/:taskId/evidence',
      body: createTaskEvidenceBodySchema,
      responses: { 201: taskEvidenceSchema, 401: errorBodySchema, 403: errorBodySchema, 404: errorBodySchema },
      metadata: routeMeta('project.task:read'),
      summary: 'Record a piece of evidence after it finished uploading',
    },

    // ---- Task extension requests --------------------------------------------------
    listTaskExtensionRequests: {
      method: 'GET',
      path: '/tasks/extension-requests',
      query: listTaskExtensionRequestsQuerySchema,
      responses: { 200: paginatedSchema(taskExtensionRequestSchema), 401: errorBodySchema },
      metadata: routeMeta(['project.task:read', 'project.task:manage']),
      summary: "The caller own extension requests, or (scope=team) their team pending ones",
    },
    requestTaskExtension: {
      method: 'POST',
      path: '/tasks/:taskId/extension-requests',
      body: requestTaskExtensionBodySchema,
      responses: {
        201: taskExtensionRequestSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        /** PM audit fix 5 — requestedDueDate is not later than the task's current dueDate. */
        422: errorBodySchema,
      },
      metadata: routeMeta('project.task:read'),
      summary: 'Ask for more time on a task; the assignee only, one pending request per task',
    },
    decideTaskExtension: {
      method: 'POST',
      path: '/tasks/extension-requests/:requestId/decide',
      body: decideTaskExtensionBodySchema,
      responses: { 200: taskExtensionRequestSchema, 401: errorBodySchema, 403: errorBodySchema, 404: errorBodySchema, 409: errorBodySchema },
      metadata: routeMeta('project.task:manage'),
      summary: 'Approve (moves the due date) or reject an extension request',
    },

    // ---- Task reminder snooze -----------------------------------------------------
    snoozeTaskReminder: {
      method: 'POST',
      path: '/tasks/:taskId/reminder/snooze',
      body: snoozeTaskReminderBodySchema,
      responses: { 200: z.object({ remindAt: instantSchema }), 401: errorBodySchema, 403: errorBodySchema, 404: errorBodySchema },
      metadata: routeMeta('project.task:read'),
      summary: '"Remind me later" on a task reminder popup — the assignee only',
    },

    // ---- KPIs ---------------------------------------------------------------------
    listKpis: {
      method: 'GET',
      path: '/kpis',
      query: listKpisQuerySchema,
      responses: { 200: z.object({ items: z.array(kpiSchema) }), 401: errorBodySchema },
      metadata: routeMeta('project.task:read'),
      summary: "The organisation KPIs, each with its progress for the current period",
    },
    createKpi: {
      method: 'POST',
      path: '/kpis',
      body: createKpiBodySchema,
      responses: { 201: kpiSchema, 401: errorBodySchema, 403: errorBodySchema, 409: errorBodySchema },
      metadata: routeMeta('org.settings:update'),
      summary: 'Create a KPI',
    },
    updateKpi: {
      method: 'PATCH',
      path: '/kpis/:kpiId',
      body: updateKpiBodySchema,
      responses: { 200: kpiSchema, 401: errorBodySchema, 403: errorBodySchema, 404: errorBodySchema },
      metadata: routeMeta('org.settings:update'),
      summary: 'Edit or deactivate a KPI',
    },

    // ---- Employee Targets (Daily Work Planning Phase 4) -----------------------------
    listEmployeeTargets: {
      method: 'GET',
      path: '/employee-targets',
      query: listEmployeeTargetsQuerySchema,
      responses: { 200: z.object({ items: z.array(employeeTargetSchema) }), 401: errorBodySchema },
      metadata: routeMeta('project.task:read'),
      summary:
        'Employee-specific targets against an existing KPI, each with achievement-to-date — ' +
        "narrowed to the caller's own + roster-reachable people unless they hold org.settings:update",
    },
    createEmployeeTarget: {
      method: 'POST',
      path: '/employee-targets',
      body: createEmployeeTargetBodySchema,
      responses: {
        201: employeeTargetSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      metadata: routeMeta('org.settings:update'),
      summary: 'Assign a target to an employee against an existing KPI — HR/Org Owner only',
    },
    updateEmployeeTarget: {
      method: 'PATCH',
      path: '/employee-targets/:employeeTargetId',
      body: updateEmployeeTargetBodySchema,
      responses: {
        200: employeeTargetSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      metadata: routeMeta('org.settings:update'),
      summary: 'Edit an employee target — HR/Org Owner only',
    },

    // ---- Next-day planning (Daily Work Planning Phase 8) ---------------------------
    getNextDayPlan: {
      method: 'GET',
      path: '/team/next-day-plan',
      query: getNextDayPlanQuerySchema,
      responses: {
        200: nextDayPlanResponseSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        /** An org-wide `project.task:manage` grant (HR/Org Owner) with no `personId` — see the
         * handler's own doc comment for why "everyone in the org" has no natural bound here. */
        422: errorBodySchema,
      },
      metadata: routeMeta('project.task:manage'),
      summary:
        "Manager planning view: each roster person's yesterday performance snapshot, today's " +
        'carry-forward candidates, and active EmployeeTargets — composition only, no new write',
    },

    // ---- Performance analytics (Daily Work Planning Phase 9 / T9.1) ----------------
    //
    // PERMISSION: `project.task:read` — same choice `listEmployeeTargets` (T4.2) already made and
    // for the identical reason: every seeded role holds it at `self` scope, which the
    // employee-reads-their-own-data case needs; a roster-only permission would not reach it.
    // ROSTER: reuses `resolveEmployeeTargetRoster` verbatim (no fourth near-duplicate helper) — see
    // that function's own doc comment in project.ts for the self+roster resolution it already does.
    getDailyPerformance: {
      method: 'GET',
      path: '/performance/:personId/daily',
      query: getDailyPerformanceQuerySchema,
      responses: {
        200: nextDayPlanSnapshotSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        /** Either the person is outside the caller's self+roster reach (same "404, identical to an
         * unknown person" posture `updateResourceAllocation` already uses), or no snapshot exists
         * for that date — never fabricated zeros for "no data". */
        404: errorBodySchema,
      },
      metadata: routeMeta('project.task:read'),
      summary: "One person's DailyPerformanceSnapshot for a given date (defaults to their today)",
    },
    getPerformanceTrend: {
      method: 'GET',
      path: '/performance/:personId/trend',
      query: getPerformanceTrendQuerySchema,
      responses: {
        200: performanceTrendResponseSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        /** Person outside the caller's self+roster reach — same posture as `getDailyPerformance`. */
        404: errorBodySchema,
        /** Inverted range, or over the 90-day cap (`resolvePerformanceTrendRange`). */
        422: errorBodySchema,
      },
      metadata: routeMeta('project.task:read'),
      summary:
        "One person's DailyPerformanceSnapshot rows across a date range, ordered by date — " +
        'empty array when none exist, never fabricated zeros',
    },

    // ---- Task reminder schedules (company-configurable) ----------------------------
    listTaskReminderSchedules: {
      method: 'GET',
      path: '/task-reminder-schedules',
      query: listTaskReminderSchedulesQuerySchema,
      responses: { 200: z.object({ items: z.array(taskReminderScheduleSchema) }), 401: errorBodySchema, 403: errorBodySchema },
      metadata: routeMeta('org.settings:update'),
      summary: 'The morning/evening task reminder schedule, org default or per legal entity',
    },
    upsertTaskReminderSchedule: {
      method: 'PUT',
      path: '/task-reminder-schedules',
      body: upsertTaskReminderScheduleBodySchema,
      responses: { 200: taskReminderScheduleSchema, 401: errorBodySchema, 403: errorBodySchema, 404: errorBodySchema },
      metadata: routeMeta('org.settings:update'),
      summary: 'Create or replace one slot (morning/evening) for the org default or one legal entity',
    },

    // ---- Analytics ------------------------------------------------------------
    getProjectAnalytics: {

      method: 'GET',
      path: '/:projectId/analytics',
      query: getProjectAnalyticsQuerySchema,
      responses: {
        200: projectAnalyticsSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      metadata: routeMeta('project.analytics:read'),
      summary: 'Hours logged vs estimated and percent-complete for a project (no money in v1)',
    },
    getProjectTimeReport: {
      method: 'GET',
      path: '/:projectId/time-report',
      query: getProjectTimeReportQuerySchema,
      responses: {
        200: projectTimeReportSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        422: errorBodySchema,
      },
      // AI-visible on purpose: the payload is bounded (top-N rows, <= 366 day rows, hours only) and
      // "who logged what on project X" is a natural copilot question. Reach-narrowed like every read.
      metadata: routeMeta('project.analytics:read'),
      summary:
        'Hours by person, task or day for one project (approved / pending / draft split, billable ' +
        'hours, and allocated-vs-logged per person), limited to the people inside the caller\'s reach',
    },

    // ---- Resource allocation / capacity planning (Phase 6) ---------------------------
    listResourceAllocations: {
      method: 'GET',
      path: '/resources',
      query: listResourceAllocationsQuerySchema,
      responses: {
        200: z.object({
          items: z.array(resourcePersonSummarySchema),
          // Personid of the last person on this page when more remain, else null — pass it back as
          // `cursor` to walk the roster; the org may hold thousands of people.
          nextCursor: z.string().nullable(),
        }),
        401: errorBodySchema,
      },
      metadata: routeMeta('project.member:manage'),
      summary:
        "Per-person allocation summary (bench/overallocated/on-leave status plus each person's " +
        'active and historical allocation rows), filterable by department, job title, project or ' +
        'minimum availability',
    },
    createResourceAllocation: {
      method: 'POST',
      path: '/resources',
      body: createResourceAllocationBodySchema,
      responses: {
        201: resourceAllocationSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        422: errorBodySchema,
      },
      metadata: routeMeta('project.member:manage'),
      summary:
        'Allocate a person to a project at a given percent; pushing the person over 100% total ' +
        'is allowed and surfaces as a warning, never a 409',
    },
    endResourceAllocation: {
      method: 'POST',
      path: '/resources/:allocationId/end',
      body: endResourceAllocationBodySchema,
      responses: {
        200: resourceAllocationSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      metadata: routeMeta('project.member:manage'),
      summary: "End a person's allocation to a project as of validTo",
    },
    updateResourceAllocation: {
      method: 'PATCH',
      path: '/resources/:allocationId',
      body: updateResourceAllocationBodySchema,
      responses: {
        200: updateResourceAllocationResponseSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        422: errorBodySchema,
      },
      // AI-visible (proposal-only like every write): the confirmation click is the guard, and an
      // over-allocation stays a warning in the response, never a rejection.
      metadata: routeMeta('project.member:manage'),
      summary:
        "Change an allocation's percent and/or end date going forward. A started row is closed the " +
        'day before the change and a new row carries the new percent — history is never rewritten',
    },
    listProjectAllocations: {
      method: 'GET',
      path: '/:projectId/allocations',
      query: listProjectAllocationsQuerySchema,
      responses: {
        200: z.object({
          items: z.array(projectAllocationSchema),
          truncated: z.boolean(),
        }),
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      metadata: routeMeta('project.member:manage'),
      summary:
        "Who is allocated to this project — percent, dates, and each person's overall availability " +
        "(limited to people inside the caller's roster)",
    },
    getResourceForecast: {
      method: 'GET',
      path: '/resources/forecast',
      query: getResourceForecastQuerySchema,
      responses: {
        200: resourceForecastSchema,
        401: errorBodySchema,
      },
      // AI-visible: bounded (<= 100 people x 12 weeks of small integers) and "who is overbooked
      // next month" is a core planning question. Default limit 25 keeps a copilot answer small.
      metadata: routeMeta('project.member:manage'),
      summary:
        'Weekly forward-looking utilisation (% allocated per week, effective-dated) for the ' +
        "caller's roster, plus this week's allocated-vs-logged hours per person",
    },
    listAllocationExclusions: {
      method: 'GET',
      path: '/resources/exclusions',
      responses: {
        200: z.object({ items: z.array(resourceAllocationExclusionSchema) }),
        401: errorBodySchema,
      },
      metadata: routeMeta('project.member:manage'),
      summary: 'Departments/job titles excluded from resource-allocation tracking entirely',
    },
    upsertAllocationExclusion: {
      method: 'POST',
      path: '/resources/exclusions',
      body: upsertAllocationExclusionBodySchema,
      responses: {
        201: resourceAllocationExclusionSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      // Exclusions are organisation-wide configuration (they hide whole departments/job titles from
      // every allocator), so writing them is org.settings:update — not every Reporting Manager.
      // Listing stays project.member:manage: the allocation page needs it to filter its picker.
      metadata: routeMeta('org.settings:update'),
      summary:
        'Exclude a department or job title from resource-allocation tracking — excluded people ' +
        'never appear in the allocate-someone picker',
    },
    removeAllocationExclusion: {
      method: 'POST',
      path: '/resources/exclusions/:exclusionId/remove',
      body: c.noBody(),
      responses: {
        200: z.object({ success: z.literal(true) }),
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      metadata: routeMeta('org.settings:update'),
      summary: 'Remove a resource-allocation exclusion',
    },

    // ---- Task dependencies / timeline (Phase 7) --------------------------------------
    listProjectDependencies: {
      method: 'GET',
      path: '/:projectId/dependencies',
      responses: {
        200: z.object({ edges: z.array(taskDependencyEdgeSchema) }),
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      metadata: routeMeta('project.task:read'),
      summary:
        'Every dependency edge of a project. In an isolated or system project only edges whose ' +
        'both tasks the caller may see are returned',
    },
    setTaskDependency: {
      method: 'POST',
      path: '/tasks/:taskId/dependencies',
      body: setTaskDependencyBodySchema,
      responses: {
        201: z.object({ edge: taskDependencyEdgeSchema }),
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        422: errorBodySchema,
      },
      metadata: routeMeta('project.task:manage'),
      summary:
        'Make a task wait on another task of the same project. 409 if the edge already exists or ' +
        'would create a cycle; 422 for a self-link, a cross-project link or an oversized graph',
    },
    removeTaskDependency: {
      method: 'POST',
      path: '/tasks/:taskId/dependencies/:dependsOnTaskId/remove',
      body: c.noBody(),
      responses: {
        200: z.object({ success: z.literal(true) }),
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      metadata: routeMeta('project.task:manage'),
      summary: 'Remove a dependency edge (404 if it does not exist)',
    },

    // ---- Single project (DECLARED LAST ON PURPOSE) -----------------------------
    // `GET /:projectId` matches any single path segment. ts-rest registers routes onto Express in
    // key order, first match wins, so declaring this ahead of `GET /tasks` made it swallow them: `projectId = 'tasks'`, a uuid lookup
    // that cannot succeed, a 500. Keep every literal-path GET above this line.
    // Enforced by apps/api/src/lib/contract-route-order.test.ts.
    getProject: {
      method: 'GET',
      path: '/:projectId',
      responses: { 200: projectSchema, 401: errorBodySchema, 404: errorBodySchema },
      metadata: routeMeta('project:read'),
      summary: 'Get one project',
    },
  },
  { pathPrefix: '/projects' },
);
